/**
 * Feature Usage Tracking Utility
 * Logs feature usage to both Firebase Analytics and backend database
 */

import { logFeatureUsage as logFirebaseFeatureUsage } from './firebaseAnalytics';
import api from '../services/api';

/**
 * Track feature usage
 * Logs to both Firebase Analytics and backend database
 */
export const trackFeatureUsage = async (
  featureName: string,
  action: string,
  screenName?: string,
  metadata?: Record<string, any>,
  userRole?: string
): Promise<void> => {
  try {
    // CRITICAL: Check if user is authenticated before making API calls
    // Import auth dynamically to avoid circular dependencies
    const { auth } = await import('../services/firebase');
    if (!auth.currentUser) {
      // User is not authenticated (e.g., during/after logout)
      // Skip backend tracking but still log to Firebase Analytics (client-side only)
      logFirebaseFeatureUsage(featureName, action, userRole);
      return;
    }

    // Log to Firebase Analytics
    logFirebaseFeatureUsage(featureName, action, userRole);

    // Log to backend database
    try {
      await api.post('/analytics/feature-usage', {
        featureName,
        action,
        platform: 'admin',
        screenName,
        metadata,
        userRole,
      });
    } catch (error: any) {
      // Silently fail - don't break the app if analytics logging fails
      // Only log non-401 errors to avoid console spam during logout
      if (error?.response?.status !== 401) {
        console.warn('Failed to log feature usage to backend:', error);
      }
    }
  } catch (error) {
    // Silently fail - don't break the app if analytics logging fails
    console.warn('Failed to track feature usage:', error);
  }
};

/**
 * Track screen view with feature context
 */
export const trackScreenView = async (
  screenName: string,
  featureName?: string,
  userRole?: string
): Promise<void> => {
  await trackFeatureUsage(
    featureName || screenName,
    'view',
    screenName,
    undefined,
    userRole
  );
};

/**
 * Track user action
 */
export const trackAction = async (
  featureName: string,
  action: string,
  screenName?: string,
  metadata?: Record<string, any>,
  userRole?: string
): Promise<void> => {
  await trackFeatureUsage(featureName, action, screenName, metadata, userRole);
};

